import { fetchRequestWrapper } from "@/lib/utilities";
import { toast } from "react-toastify";
import priceTabStore, {
  defaultConfigTab,
  IPriceConfigTab,
  isAtPriceConfigLimit,
  MAX_PRICE_CONFIGS,
} from "./pricesTabStore";

function generateNewPricesConfig(): IPriceConfigTab {
  return {
    id: 0,
    displayName: "New View",
    configJson: "",
  };
}

export async function initialPricesConfigsFromAPI(
  getAccessTokenSilently: Function,
) {
  const priceConfigResponse = await fetchRequestWrapper(
    { url: "/UserSettings/PriceWidget/Configs", method: "GET" },
    getAccessTokenSilently,
  );

  const configs = priceConfigResponse.configs;

  if (configs.length == 0) {
    // this occurs for new users, so create a default tab.
    configs.push(defaultConfigTab);
    priceTabStore.getState().setPriceConfigTabs(configs);
  } else {
    configs.splice(0, 0, defaultConfigTab); // Add to front
    priceTabStore.getState().setPriceConfigTabs(configs);
  }
}

export interface IUpdatePriceConfigInStoreAndAPI {
  newName: string;
  config: IPriceConfigTab;
  getAccessTokenSilently: Function;
}

export async function updateDisplayNamePriceConfigInStoreAndAPI({
  newName,
  config,
  getAccessTokenSilently,
}: IUpdatePriceConfigInStoreAndAPI) {
  const newSettingsConfigs = priceTabStore
    .getState()
    .priceConfigTabs.map((tab) =>
      tab.id === config.id
        ? {
            ...tab,
            displayName: newName!,
          }
        : tab,
    );

  const configReadyForDB = newSettingsConfigs.find(
    (configSearched) => configSearched.id == config.id,
  );

  try {
    await fetchRequestWrapper(
      {
        url: `/UserSettings/PriceWidget/Configs`,
        method: "PUT",
        objToSend: configReadyForDB,
      },
      getAccessTokenSilently,
    );
    priceTabStore.getState().setPriceConfigTabs(newSettingsConfigs);
  } catch {
    toast.error(
      "An issue has occurred with updating the display name of the view, please contact support.",
    );
  }
}

export interface IUpdateConfigJSONPriceConfigInStoreAndAPI {
  configToSendViaAPI: IPriceConfigTab;
  getAccessTokenSilently: Function;
}

export async function updateConfigJSONPriceConfigInStoreAndAPI({
  configToSendViaAPI,
  getAccessTokenSilently,
}: IUpdateConfigJSONPriceConfigInStoreAndAPI) {
  const newSettingsConfigs = priceTabStore
    .getState()
    .priceConfigTabs.map((tab) =>
      tab.id === configToSendViaAPI.id
        ? {
            ...configToSendViaAPI,
          }
        : tab,
    );

  try {
    await fetchRequestWrapper(
      {
        url: `/UserSettings/PriceWidget/Configs`,
        method: "PUT",
        objToSend: configToSendViaAPI,
      },
      getAccessTokenSilently,
    );
    priceTabStore.getState().setPriceConfigTabs(newSettingsConfigs);
  } catch {
    toast.error(
      "An issue has occurred with updating the view, please contact support.",
    );
  }
}

interface IaddNewPriceConfigToStoreAndSetAsActive {
  setActivePricesConfigIdNumber: Function;
  getAccessTokenSilently: Function;
}

export async function addNewPriceConfigToStoreAndSetAsActive({
  setActivePricesConfigIdNumber,
  getAccessTokenSilently,
}: IaddNewPriceConfigToStoreAndSetAsActive) {
  // Enforced here, not just on the button, so the limit holds however this is reached.
  if (isAtPriceConfigLimit(priceTabStore.getState().priceConfigTabs)) {
    toast.error(
      `You can only have ${MAX_PRICE_CONFIGS} views. Delete one to add another.`,
    );
    return;
  }

  const newSettingsConfigs = priceTabStore
    .getState()
    .priceConfigTabs.map((tab) => {
      return { ...tab };
    });

  const newConfig = generateNewPricesConfig();

  // call DB here
  const responseFromAPI = await fetchRequestWrapper(
    {
      url: "/UserSettings/PriceWidget/Configs",
      method: "POST",
      objToSend: newConfig,
    },
    getAccessTokenSilently,
  );
  newSettingsConfigs.push(responseFromAPI); // USE the response to avoid the temp id making the DB
  priceTabStore.getState().setPriceConfigTabs(newSettingsConfigs);
  // Uses the passed in hook to set on a widget level
  setActivePricesConfigIdNumber(responseFromAPI.id);
}

interface IcopyExistingConfigToStoreAndSetAsActive {
  activePricesConfigIdNumber: number;
  setActivePricesConfigIdNumber: Function;
  getAccessTokenSilently: Function;
}
export async function copyExistingConfigToStoreAndSetAsActive({
  activePricesConfigIdNumber,
  setActivePricesConfigIdNumber,
  getAccessTokenSilently,
}: IcopyExistingConfigToStoreAndSetAsActive) {
  // Enforced here, not just on the button, so the limit holds however this is reached.
  if (isAtPriceConfigLimit(priceTabStore.getState().priceConfigTabs)) {
    toast.error(
      `You can only have ${MAX_PRICE_CONFIGS} views. Delete one to add another.`,
    );
    return;
  }

  const newSettingsConfigs = priceTabStore
    .getState()
    .priceConfigTabs.map((tab) => {
      return { ...tab };
    });

  const configToCopy = priceTabStore
    .getState()
    .priceConfigTabs.find(
      (existingConfigs) => existingConfigs.id == activePricesConfigIdNumber,
    );

  // A new object - renaming the found one in place edited the config being copied.
  const newConfig = configToCopy
    ? { ...configToCopy, displayName: "New View" }
    : undefined;
  // call DB here
  const responseFromAPI = await fetchRequestWrapper(
    {
      url: "/UserSettings/PriceWidget/Configs",
      method: "POST",
      objToSend: newConfig,
    },
    getAccessTokenSilently,
  );

  newSettingsConfigs.push(responseFromAPI); // USE the response to avoid the temp id making the DB
  priceTabStore.getState().setPriceConfigTabs(newSettingsConfigs);
  // Uses the passed in hook to set on a widget level
  setActivePricesConfigIdNumber(responseFromAPI.id);
}

export interface IremovePriceConfigFromStoreAndDatabase {
  config: IPriceConfigTab;
  activePricesConfigIdNumber: number;
  setActivePricesConfigIdNumber: Function;
  getAccessTokenSilently: Function;
}

// When deleting a config.
export async function removePriceConfigFromStoreAndDatabase({
  config,
  activePricesConfigIdNumber,
  setActivePricesConfigIdNumber,
  getAccessTokenSilently,
}: IremovePriceConfigFromStoreAndDatabase) {
  // Filter and update the store

  const newSettingsConfigs = priceTabStore
    .getState()
    .priceConfigTabs.filter((tab) => tab.id != config.id);

  // If a user deletes a tab they are not on, keep their current tab active.
  //If the user deletes a tab they ARE on, set them back to the default tab.
  if (config.id == activePricesConfigIdNumber) {
    setActivePricesConfigIdNumber(-1);
  }

  try {
    await fetchRequestWrapper(
      {
        url: `/UserSettings/PriceWidget/Configs/${config.id}`,
        method: "DELETE",
        ignoreResponse: true,
      },
      getAccessTokenSilently,
    );
    priceTabStore.getState().setPriceConfigTabs(newSettingsConfigs);
  } catch {
    toast.error(
      "An issue has occurred with deleting the view, please contact support.",
    );
  }
}

// When updating a config - Such as the Display Name or Config.
